/**
 * Authenticated calls from the new console to the server, with the signed-in staff member's token. Throws with the
 * server's message so pages can show it.
 */
import { supabaseAdmin } from '@/integrations/supabase/client';

export async function stagingFetch<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}), Authorization: `Bearer ${session.access_token}` }
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || body?.error || `HTTP ${res.status}`);
  return body as T;
}

/** Fetch a protected file (contract PDF, booking record) with the staff token and open or download it. */
export async function stagingOpenFile(path: string, filename: string, download = false) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');
  const res = await fetch(path, { headers: { Authorization: `Bearer ${session.access_token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message || body?.error || `HTTP ${res.status}`);
  }
  const url = URL.createObjectURL(await res.blob());
  if (download) {
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const mountainToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

/** "Sep 26, 2026" from YYYY-MM-DD (no time-zone shift). */
export function shortDate(iso?: string | null) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Opens the stored COI for an insurance request (a 5-minute signed link). */
export async function openCoi(id: string, download = false) {
  const r = await stagingFetch(`/api/admin/staging/insurance/${id}/coi${download ? '?download=1' : ''}`);
  window.open(r.url, '_blank', 'noopener');
}
